package dev.alimmz.portfolio.presentation.screen

import alimmzdevportfolio.shared.generated.resources.Me
import alimmzdevportfolio.shared.generated.resources.Res
import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.onPointerEvent
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.alimmz.portfolio.domain.model.Project
import dev.alimmz.portfolio.domain.model.Skill
import dev.alimmz.portfolio.presentation.PortfolioViewModel
import dev.alimmz.portfolio.presentation.theme.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun PortfolioScreen(
    viewModel: PortfolioViewModel = PortfolioViewModel(),
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit
) {
    val state by viewModel.state.collectAsState()
    var selectedProject by remember { mutableStateOf<Project?>(null) }

    selectedProject?.let { project ->
        ProjectDetailScreen(
            project = project,
            onBack = { selectedProject = null }
        )
        return
    }

    var mousePosition by remember { mutableStateOf(Offset.Zero) }
    val lazyListState = rememberLazyListState()
    val scope = rememberCoroutineScope()

    // These are the LazyColumn item indexes of the sections below the hero.
    val journeyIndex = 1
    val projectsIndex = 2 + state.experiences.size
    val expertiseIndex = 4 + state.experiences.size

    val isScrolled by remember {
        derivedStateOf {
            lazyListState.firstVisibleItemIndex > 0 ||
                    lazyListState.firstVisibleItemScrollOffset > 0
        }
    }

    fun scrollToSection(index: Int) {
        scope.launch {
            lazyListState.animateScrollToItem(
                index = index,
                scrollOffset = -20
            )
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .onPointerEvent(PointerEventType.Move) {
                mousePosition = it.changes.first().position
            }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val isWide = maxWidth > 900.dp
            val isCompact = maxWidth < 480.dp
            val horizontalPadding = when {
                maxWidth > 1400.dp -> 120.dp
                isWide -> 72.dp
                isCompact -> 16.dp
                else -> 24.dp
            }

            LazyColumn(
                state = lazyListState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(
                    top = if (isCompact) 88.dp else 112.dp,
                    bottom = if (isCompact) 72.dp else 120.dp
                ),
                verticalArrangement = Arrangement.spacedBy(
                    if (isWide) 96.dp else if (isCompact) 56.dp else 72.dp
                )
            ) {
                item {
                    StudioHero(state.profile, horizontalPadding, isWide, isCompact)
                    EngineeringSnapshot(horizontalPadding, isWide)
                }

                item {
                    StudioSectionHeader(
                        title = "JOURNEY",
                        eyebrow = "EXPERIENCE",
                        description = "A concise timeline of the teams, products and responsibilities that shaped my engineering approach.",
                        horizontalPadding = horizontalPadding,
                        isWide = isWide
                    )
                }

                itemsIndexed(state.experiences) { _, experience ->
                    StudioExperienceItem(experience, horizontalPadding, isWide)
                }

                item {
                    StudioSectionHeader(
                        title = "SELECTED PROJECTS",
                        eyebrow = "SELECTED WORK",
                        description = "A focused collection of Android work, product experiments and engineering projects.",
                        horizontalPadding = horizontalPadding,
                        isWide = isWide
                    )
                }

                item {
                    StudioProjectsGrid(
                        projects = state.projects,
                        horizontalPadding = horizontalPadding,
                        isWide = isWide,
                        onProjectClick = { selectedProject = it }
                    )
                }

                item {
                    StudioSectionHeader(
                        title = "EXPERTISE",
                        eyebrow = "WHAT I BUILD",
                        description = "Core technologies and engineering skills I use to build reliable mobile products.",
                        horizontalPadding = horizontalPadding,
                        isWide = isWide
                    )
                    StudioSkills(
                        skills = state.skills,
                        horizontalPadding = horizontalPadding,
                        isWide = isWide
                    )
                }

                item {
                    StudioFooter(state.profile, horizontalPadding, isWide)
                }
            }

            StudioNavbar(
                profile = state.profile,
                horizontalPadding = horizontalPadding,
                isScrolled = isScrolled,
                isWide = isWide,
                isCompact = isCompact,
                isDarkTheme = isDarkTheme,
                onThemeToggle = onThemeToggle,
                onWorkClick = { scrollToSection(projectsIndex) },
                onExpertiseClick = { scrollToSection(expertiseIndex) },
                onJourneyClick = { scrollToSection(journeyIndex) }
            )

            // Keep the native pointer unobstructed.
        }
    }
}

@Composable
fun StudioNavbar(
    profile: dev.alimmz.portfolio.domain.model.Profile?,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isScrolled: Boolean,
    isWide: Boolean,
    isCompact: Boolean,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onWorkClick: () -> Unit,
    onExpertiseClick: () -> Unit,
    onJourneyClick: () -> Unit
) {
    val navAlpha by animateFloatAsState(
        targetValue = if (isScrolled) 0.98f else 0.82f,
        animationSpec = tween(500),
        label = "navbar_alpha"
    )
    val heightAnim by animateDpAsState(
        targetValue = if (isScrolled) 64.dp else if (isCompact) 84.dp else 104.dp,
        animationSpec = tween(300),
        label = "navbar_height"
    )

    val infiniteTransition = rememberInfiniteTransition(label = "status")
    val pulseAlpha by infiniteTransition.animateFloat(
        initialValue = 0.35f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(1200, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "pulse"
    )
    val headerDrift by infiniteTransition.animateFloat(
        initialValue = -90f,
        targetValue = 90f,
        animationSpec = infiniteRepeatable(
            animation = tween(6000, easing = EaseInOutSine),
            repeatMode = RepeatMode.Reverse
        ),
        label = "header_drift"
    )
    val brandFloat by infiniteTransition.animateFloat(
        initialValue = -1.5f,
        targetValue = 1.5f,
        animationSpec = infiniteRepeatable(
            animation = tween(1900, easing = EaseInOutSine),
            repeatMode = RepeatMode.Reverse
        ),
        label = "brand_float"
    )

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .height(heightAnim),
        color = MaterialTheme.colorScheme.background.copy(alpha = navAlpha),
        shadowElevation = if (isScrolled) 10.dp else 0.dp
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .offset(x = (-40).dp, y = (-36).dp)
                    .graphicsLayer { translationX = headerDrift }
                    .size(150.dp)
                    .clip(CircleShape)
                    .background(AndroidGreen.copy(alpha = 0.10f))
            )
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .offset(x = 28.dp, y = 44.dp)
                    .graphicsLayer { translationX = -headerDrift * 0.4f }
                    .size(120.dp)
                    .clip(CircleShape)
                    .background(MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.45f))
            )

            Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(
                    horizontal = horizontalPadding,
                    vertical = if (isScrolled) 10.dp else 16.dp
                )
                .then(
                    if (isScrolled) {
                        Modifier
                    } else {
                        Modifier
                            .clip(RoundedCornerShape(22.dp))
                            .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.76f))
                            .border(1.dp, MaterialTheme.colorScheme.onBackground.copy(alpha = 0.08f), RoundedCornerShape(22.dp))
                    }
                )
                .padding(horizontal = if (isWide) 22.dp else if (isCompact) 10.dp else 14.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.graphicsLayer { translationY = brandFloat }) {
                Text(
                    "ALIMMZ.DEV",
                    style = if (isWide) MaterialTheme.typography.titleMedium else MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Black,
                    letterSpacing = (-1).sp,
                    color = MaterialTheme.colorScheme.onBackground
                )
                Text(
                    if (isCompact) "ANDROID ENGINEER" else (profile?.role ?: "ANDROID ENGINEER").uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = AndroidGreen,
                    fontSize = if (isWide) 8.sp else 7.sp,
                    letterSpacing = 2.sp
                )
            }

            if (isWide) {
                Row(horizontalArrangement = Arrangement.spacedBy(if (isScrolled) 20.dp else 32.dp)) {
                    EditorialNavItem("JOURNEY", onJourneyClick)
                    EditorialNavItem("WORK", onWorkClick)
                    EditorialNavItem("EXPERTISE", onExpertiseClick)
                }
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(if (isWide) 14.dp else 8.dp)
            ) {
                if (isWide) {
                    Text(
                        "AVAILABLE",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        letterSpacing = 1.sp
                    )
                }

                Box(
                    modifier = Modifier
                        .size(if (isWide) 8.dp else 6.dp)
                        .graphicsLayer { alpha = pulseAlpha }
                        .clip(CircleShape)
                        .background(AndroidGreen)
                )

                ThemeToggleButton(
                    isDarkTheme = isDarkTheme,
                    isWide = isWide,
                    onClick = onThemeToggle
                )
                ResumeButton(isWide, isCompact)
            }
        }
        }
    }
}

@Composable
fun EditorialNavItem(
    text: String,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    val textColor by animateColorAsState(
        if (isHovered) AndroidGreen else MaterialTheme.colorScheme.onBackground,
        animationSpec = tween(180),
        label = "nav_color"
    )
    val lineWidth by animateDpAsState(
        if (isHovered) 26.dp else 0.dp,
        animationSpec = tween(180),
        label = "nav_line"
    )

    Column(
        modifier = Modifier
            .hoverable(interactionSource)
            .clickable(onClick = onClick),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Black,
            color = textColor,
            letterSpacing = 2.sp
        )

        Spacer(modifier = Modifier.height(6.dp))

        Box(
            modifier = Modifier
                .height(2.dp)
                .width(lineWidth)
                .clip(RoundedCornerShape(2.dp))
                .background(AndroidGreen)
        )
    }
}

private const val CONTACT_URL = "mailto:Ali.Mohammadzadeh.Dev@outlook.com"

@Composable
private fun ThemeToggleButton(
    isDarkTheme: Boolean,
    isWide: Boolean,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    val toggleWidth = if (isWide) 72.dp else 68.dp
    val knobOffset by animateDpAsState(
        targetValue = if (isDarkTheme) toggleWidth - 36.dp else 0.dp,
        animationSpec = spring(dampingRatio = 0.72f, stiffness = Spring.StiffnessMedium),
        label = "theme_knob"
    )
    val knobColor = MaterialTheme.colorScheme.surface

    Surface(
        modifier = Modifier
            .width(toggleWidth)
            .height(36.dp)
            .hoverable(interactionSource)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        shape = RoundedCornerShape(50),
        color = if (isHovered) {
            AndroidGreen.copy(alpha = 0.24f)
        } else {
            MaterialTheme.colorScheme.surfaceVariant
        },
        border = BorderStroke(
            1.dp,
            if (isHovered) {
                AndroidGreen.copy(alpha = 0.8f)
            } else {
                MaterialTheme.colorScheme.outline.copy(alpha = 0.55f)
            }
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(3.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Box(
                modifier = Modifier
                    .offset(x = knobOffset)
                    .size(30.dp)
                    .shadow(3.dp, CircleShape)
                    .clip(CircleShape)
                    .background(knobColor),
                contentAlignment = Alignment.Center
            ) {
                ThemeModeIcon(
                    isDarkTheme = isDarkTheme,
                    color = AndroidGreen,
                    cutoutColor = knobColor
                )
            }
        }
    }
}

@Composable
private fun ThemeModeIcon(
    isDarkTheme: Boolean,
    color: Color,
    cutoutColor: Color
) {
    Canvas(modifier = Modifier.size(16.dp)) {
        if (isDarkTheme) {
            drawCircle(color = color, radius = size.minDimension * 0.38f)
            drawCircle(
                color = cutoutColor,
                radius = size.minDimension * 0.34f,
                center = Offset(size.width * 0.64f, size.height * 0.34f)
            )
        } else {
            val center = Offset(size.width / 2f, size.height / 2f)
            drawCircle(color = color, radius = size.minDimension * 0.22f, center = center)
            repeat(8) { index ->
                val angle = index * kotlin.math.PI.toFloat() / 4f
                val innerRadius = size.minDimension * 0.34f
                val outerRadius = size.minDimension * 0.47f
                drawLine(
                    color = color,
                    start = Offset(
                        center.x + kotlin.math.cos(angle) * innerRadius,
                        center.y + kotlin.math.sin(angle) * innerRadius
                    ),
                    end = Offset(
                        center.x + kotlin.math.cos(angle) * outerRadius,
                        center.y + kotlin.math.sin(angle) * outerRadius
                    ),
                    strokeWidth = 1.4.dp.toPx(),
                    cap = StrokeCap.Round
                )
            }
        }
    }
}

@Composable
private fun ResumeButton(isWide: Boolean, isCompact: Boolean) {
    val uriHandler = LocalUriHandler.current
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    Surface(
        modifier = Modifier
            .hoverable(interactionSource)
            .clickable(
                interactionSource = interactionSource,
                indication = null
            ) {
                uriHandler.openUri(CONTACT_URL)
            },
        shape = RoundedCornerShape(50),
        color = if (isHovered) AndroidGreen else Color.Transparent,
        border = BorderStroke(
            1.dp,
            if (isHovered) AndroidGreen else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.10f)
        )
    ) {
        Text(
            text = if (isWide) "GET IN TOUCH" else if (isCompact) "EMAIL" else "CONTACT",
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Black,
            color = if (isHovered) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onBackground,
            letterSpacing = 0.8.sp,
            modifier = Modifier.padding(
                horizontal = if (isWide) 14.dp else if (isCompact) 8.dp else 10.dp,
                vertical = if (isWide) 9.dp else if (isCompact) 5.dp else 6.dp
            )
        )
    }
}

@Composable
fun StudioHero(
    profile: dev.alimmz.portfolio.domain.model.Profile?,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean,
    isCompact: Boolean
) {
    val infiniteTransition = rememberInfiniteTransition(label = "hero")
    val floatAnim by infiniteTransition.animateFloat(
        initialValue = -12f,
        targetValue = 12f,
        animationSpec = infiniteRepeatable(
            animation = tween(3000, easing = EaseInOutSine),
            repeatMode = RepeatMode.Reverse
        ),
        label = "hero_float"
    )
    val ambientDrift by infiniteTransition.animateFloat(
        initialValue = -32f,
        targetValue = 32f,
        animationSpec = infiniteRepeatable(
            animation = tween(5200, easing = EaseInOutSine),
            repeatMode = RepeatMode.Reverse
        ),
        label = "hero_ambient_drift"
    )
    val glowAlpha by infiniteTransition.animateFloat(
        initialValue = 0.18f,
        targetValue = 0.38f,
        animationSpec = infiniteRepeatable(
            animation = tween(2400, easing = EaseInOutSine),
            repeatMode = RepeatMode.Reverse
        ),
        label = "hero_glow"
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = horizontalPadding)
            .heightIn(min = if (isWide) 650.dp else 0.dp)
            .clip(RoundedCornerShape(if (isWide) 32.dp else 24.dp))
            .background(Brush.linearGradient(listOf(MaterialTheme.colorScheme.surfaceVariant, MaterialTheme.colorScheme.primaryContainer)))
            .border(1.dp, MaterialTheme.colorScheme.onBackground.copy(alpha = 0.07f), RoundedCornerShape(if (isWide) 32.dp else 24.dp))
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .offset(x = if (isWide) 88.dp else 36.dp, y = (-72).dp)
                .graphicsLayer {
                    translationX = ambientDrift
                    alpha = glowAlpha
                }
                .size(if (isWide) 390.dp else 250.dp)
                .clip(CircleShape)
                .background(AndroidGreen)
        )
        Box(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .offset(x = (-90).dp, y = 90.dp)
                .graphicsLayer { translationX = -ambientDrift * 0.55f }
                .size(if (isWide) 310.dp else 210.dp)
                .clip(CircleShape)
                .background(MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.8f))
        )

        Column(
            modifier = Modifier
                .then(if (isWide) Modifier.fillMaxSize() else Modifier.fillMaxWidth())
                .padding(if (isWide) 48.dp else if (isCompact) 20.dp else 26.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "PORTFOLIO / 2026",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Black,
                    letterSpacing = 2.sp,
                    color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.5f)
                )
                if (!isCompact) Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(8.dp)
                            .clip(CircleShape)
                            .graphicsLayer { alpha = glowAlpha }
                            .background(AndroidGreen)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        text = "AVAILABLE FOR WORK",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Black,
                        letterSpacing = 1.sp,
                        color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.55f)
                    )
                }
            }

            Spacer(modifier = Modifier.height(if (isWide) 56.dp else if (isCompact) 28.dp else 36.dp))

            Text(
                text = (profile?.name ?: "ALI MOHAMMADZADEH").uppercase(),
                style = MaterialTheme.typography.displayLarge.copy(
                    fontSize = if (isWide) 88.sp else if (isCompact) 38.sp else 44.sp,
                    lineHeight = if (isWide) 94.sp else if (isCompact) 43.sp else 50.sp,
                    fontWeight = FontWeight.Black,
                    letterSpacing = if (isWide) (-6).sp else (-2.5).sp
                ),
                color = MaterialTheme.colorScheme.onBackground,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(if (isWide) 44.dp else if (isCompact) 24.dp else 30.dp))

            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HeroCopy(profile, true, modifier = Modifier.weight(1f))

                    Box(
                        modifier = Modifier
                            .padding(start = 60.dp)
                            .graphicsLayer { translationY = floatAnim }
                    ) {
                        HeroImage(size = 300.dp)
                    }
                }
            } else {
                Column(modifier = Modifier.fillMaxWidth()) {
                    HeroCopy(profile, false, isCompact, Modifier.fillMaxWidth())

                    Spacer(modifier = Modifier.height(if (isCompact) 24.dp else 28.dp))

                    Box(
                        modifier = Modifier
                            .align(Alignment.CenterHorizontally)
                            .graphicsLayer { translationY = floatAnim }
                    ) {
                        HeroImage(size = if (isCompact) 144.dp else 170.dp)
                    }
                }
            }
        }
    }
}

@Composable
private fun HeroCopy(
    profile: dev.alimmz.portfolio.domain.model.Profile?,
    isWide: Boolean,
    isCompact: Boolean = false,
    modifier: Modifier
) {
    Column(modifier = modifier) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(if (isWide) 48.dp else if (isCompact) 24.dp else 32.dp, 4.dp)
                    .background(AndroidGreen)
            )
            Spacer(modifier = Modifier.width(if (isCompact) 10.dp else 16.dp))
            Text(
                text = (profile?.role ?: "ANDROID ENGINEER").uppercase(),
            style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = AndroidGreen,
            fontSize = if (isCompact) 14.sp else 22.sp,
            letterSpacing = if (isCompact) 1.5.sp else 4.sp
            )
        }

        Spacer(modifier = Modifier.height(if (isCompact) 20.dp else 32.dp))

        Text(
            text = profile?.bio ?: "",
            style = MaterialTheme.typography.bodyLarge.copy(
                fontSize = if (isWide) 22.sp else if (isCompact) 16.sp else 18.sp,
                lineHeight = if (isWide) 34.sp else if (isCompact) 24.sp else 28.sp
            ),
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.widthIn(max = 680.dp)
        )

        Spacer(modifier = Modifier.height(if (isCompact) 24.dp else 40.dp))

        val uriHandler = LocalUriHandler.current
        Row(
            horizontalArrangement = Arrangement.spacedBy(if (isCompact) 14.dp else 20.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SocialHeroLink("GITHUB") { profile?.githubUrl?.let { uriHandler.openUri(it) } }
            SocialHeroLink("LINKEDIN") { profile?.linkedinUrl?.let { uriHandler.openUri(it) } }
            SocialHeroLink("EMAIL") { profile?.email?.let { uriHandler.openUri("mailto:$it") } }
        }

        Spacer(modifier = Modifier.height(if (isCompact) 24.dp else 40.dp))

        Text(
            if (isCompact) "KOTLIN  •  COMPOSE" else "KOTLIN  •  COMPOSE  •  MOBILE PRODUCTS",
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Black,
            color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.4f),
            fontSize = if (isCompact) 10.sp else 12.sp,
            letterSpacing = if (isCompact) 1.sp else 2.sp
        )
    }
}

@Composable
private fun SocialHeroLink(
    text: String,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    Text(
        text = text,
        style = MaterialTheme.typography.labelLarge,
        fontWeight = FontWeight.Black,
        color = if (isHovered) AndroidGreen else MaterialTheme.colorScheme.onBackground,
        modifier = Modifier
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            )
            .padding(vertical = 4.dp)
    )
}

@Composable
private fun HeroImage(size: androidx.compose.ui.unit.Dp) {
    Box(
        modifier = Modifier
            .size(size)
            .shadow(28.dp, CircleShape, ambientColor = AndroidGreen.copy(alpha = 0.14f))
            .clip(CircleShape)
            .border(1.dp, AndroidGreen.copy(alpha = 0.22f), CircleShape)
            .background(
                Brush.radialGradient(
                    colors = listOf(MaterialTheme.colorScheme.surface, MaterialTheme.colorScheme.surfaceVariant)
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Image(
            painter = painterResource(Res.drawable.Me),
            contentDescription = "Profile Picture",
            modifier = Modifier
                .fillMaxSize()
                .clip(CircleShape)
        )
    }
}

@Composable
fun StudioSectionHeader(
    title: String,
    eyebrow: String,
    description: String,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean
) {
    Column(
        modifier = Modifier.padding(horizontal = horizontalPadding)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(6.dp)
                    .clip(CircleShape)
                    .background(AndroidGreen)
            )
            Text(
                eyebrow,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                letterSpacing = 2.sp,
                color = AndroidGreen
            )
        }

        Spacer(modifier = Modifier.height(10.dp))

        Text(
            title,
            style = if (isWide) MaterialTheme.typography.headlineLarge.copy(
                fontWeight = FontWeight.Black,
                letterSpacing = (-1.5).sp
            ) else MaterialTheme.typography.headlineMedium.copy(
                fontWeight = FontWeight.Black,
                letterSpacing = (-1).sp
            ),
            color = MaterialTheme.colorScheme.onBackground
        )

        Spacer(modifier = Modifier.height(10.dp))

        Text(
            description,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            lineHeight = 23.sp,
            modifier = Modifier.widthIn(max = 620.dp)
        )

        Spacer(modifier = Modifier.height(20.dp))

        HorizontalDivider(color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.08f))
    }
}

@Composable
fun StudioSkills(
    skills: List<Skill>,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = horizontalPadding)
    ) {
        Spacer(modifier = Modifier.height(28.dp))

        val categoryOrder = listOf(
            "Android Development",
            "Architecture & Engineering",
            "Data, Networking & Testing",
            "Tools, Platform & Productivity"
        )

        val groups = categoryOrder.mapNotNull { category ->
            skills.filter { it.category == category }
                .takeIf { it.isNotEmpty() }
                ?.let { category to it }
        }

        if (isWide) {
            groups.chunked(2).forEachIndexed { rowIndex, rowGroups ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(18.dp)
                ) {
                    rowGroups.forEachIndexed { columnIndex, group ->
                        SkillCategoryPanel(
                            category = group.first,
                            skills = group.second,
                            index = rowIndex * 2 + columnIndex + 1,
                            modifier = Modifier.weight(1f)
                        )
                    }
                    if (rowGroups.size == 1) Spacer(modifier = Modifier.weight(1f))
                }
                if (rowIndex < groups.chunked(2).lastIndex) Spacer(modifier = Modifier.height(18.dp))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                groups.forEachIndexed { index, group ->
                    SkillCategoryPanel(
                        category = group.first,
                        skills = group.second,
                        index = index + 1,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }
    }
}

@Composable
private fun SkillCategoryPanel(
    category: String,
    skills: List<Skill>,
    index: Int,
    modifier: Modifier = Modifier
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    val panelColor by animateColorAsState(
        targetValue = if (isHovered) MaterialTheme.colorScheme.surface else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.62f),
        animationSpec = tween(180),
        label = "skills_panel_color"
    )

    Surface(
        modifier = modifier
            .hoverable(interactionSource)
            .border(
                width = 1.dp,
                color = if (isHovered) AndroidGreen.copy(alpha = 0.7f) else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.09f),
                shape = RoundedCornerShape(20.dp)
            ),
        shape = RoundedCornerShape(20.dp),
        color = panelColor,
        shadowElevation = 0.dp
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(22.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "0$index",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Black,
                    letterSpacing = 1.5.sp,
                    color = AndroidGreen
                )
                Text("${skills.size} SKILLS", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurfaceVariant, letterSpacing = 1.sp)
            }

            Spacer(modifier = Modifier.height(18.dp))

            Text(
                text = category,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Black,
                color = MaterialTheme.colorScheme.onBackground,
                lineHeight = 30.sp
            )

            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.08f))
            Spacer(modifier = Modifier.height(16.dp))

            skills.chunked(2).forEachIndexed { rowIndex, rowSkills ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    rowSkills.forEach { skill -> SkillChip(skill.name, Modifier.weight(1f)) }
                    if (rowSkills.size == 1) Spacer(modifier = Modifier.weight(1f))
                }
                if (rowIndex < skills.chunked(2).lastIndex) Spacer(modifier = Modifier.height(8.dp))
            }
        }
    }
}

@Composable
private fun SkillChip(name: String, modifier: Modifier = Modifier) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    Surface(
        modifier = modifier.hoverable(interactionSource),
        shape = RoundedCornerShape(10.dp),
        color = if (isHovered) AndroidGreen.copy(alpha = 0.18f) else MaterialTheme.colorScheme.surface.copy(alpha = 0.7f),
        border = BorderStroke(1.dp, if (isHovered) AndroidGreen.copy(alpha = 0.55f) else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.07f))
    ) {
        Text(
            text = name,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 9.dp),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onBackground,
            maxLines = 2,
            lineHeight = 16.sp
        )
    }
}

@Composable
fun StudioProjectsGrid(
    projects: List<Project>,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean,
    onProjectClick: (Project) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = horizontalPadding)
    ) {
        if (isWide) {
            projects.chunked(2).forEach { rowProjects ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    rowProjects.forEach { project ->
                        Box(modifier = Modifier.weight(1f)) {
                            StudioProjectItem(
                                project = project,
                                index = projects.indexOf(project),
                                isWide = true,
                                onClick = { onProjectClick(project) }
                            )
                        }
                    }
                    if (rowProjects.size == 1) {
                        Spacer(modifier = Modifier.weight(1f))
                    }
                }
                Spacer(modifier = Modifier.height(24.dp))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                projects.forEachIndexed { index, project ->
                    StudioProjectItem(
                        project = project,
                        index = index,
                        isWide = false,
                        onClick = { onProjectClick(project) }
                    )
                }
            }
        }
    }
}

@Composable
fun StudioProjectItem(
    project: Project,
    index: Int,
    isWide: Boolean,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    val scale by animateFloatAsState(
        targetValue = if (isHovered) 1.01f else 1f,
        animationSpec = spring(stiffness = Spring.StiffnessLow),
        label = "project_scale"
    )

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .hoverable(interactionSource)
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surface.copy(alpha = 0.6f),
        border = BorderStroke(
            1.dp,
            if (isHovered) AndroidGreen.copy(alpha = 0.5f) else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.06f)
        ),
        shadowElevation = 0.dp
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            ProjectArtwork(project, Modifier.fillMaxWidth().height(if (isWide) 310.dp else 250.dp))

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = project.technologies.firstOrNull()?.uppercase() ?: "ANDROID",
                        style = MaterialTheme.typography.labelSmall,
                        color = AndroidGreen,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 1.5.sp
                    )

                    ProjectIndex(index)
                }

                Spacer(modifier = Modifier.height(12.dp))

                Text(
                    text = project.title,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Black,
                    color = MaterialTheme.colorScheme.onBackground
                )

                Spacer(modifier = Modifier.height(8.dp))

                Text(
                    text = project.description,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    lineHeight = 22.sp,
                    maxLines = 2
                )

                Spacer(modifier = Modifier.height(20.dp))

                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    project.technologies.drop(1).take(3).forEach { tech ->
                        Surface(
                            color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.04f),
                            shape = RoundedCornerShape(4.dp)
                        ) {
                            Text(
                                text = tech.uppercase(),
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontSize = 9.sp,
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProjectIndex(index: Int) {
    Text(
        text = (index + 1).toString().padStart(2, '0'),
        style = MaterialTheme.typography.labelMedium,
        fontWeight = FontWeight.Bold,
        color = AndroidGreen,
        letterSpacing = 1.sp
    )
}

@Composable
fun StudioExperienceItem(
    experience: dev.alimmz.portfolio.domain.model.Experience,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean
) {
    if (isWide) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = horizontalPadding),
            horizontalArrangement = Arrangement.spacedBy(20.dp),
            verticalAlignment = Alignment.Top
        ) {
            Column(
                modifier = Modifier
                    .width(168.dp)
                    .padding(top = 18.dp)
            ) {
                Text(
                    text = experience.period,
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    lineHeight = 20.sp
                )
            }

            ExperienceTimelineMarker(experience.id)
            ExperienceCard(experience, Modifier.weight(1f), isWide = true)
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = horizontalPadding)
        ) {
            Text(
                text = experience.period,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                color = AndroidGreen,
                letterSpacing = 0.8.sp,
                lineHeight = 18.sp
            )

            Spacer(modifier = Modifier.height(10.dp))
            ExperienceCard(experience, Modifier.fillMaxWidth(), isWide = false)
        }
    }
}

@Composable
private fun ExperienceTimelineMarker(id: String) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.padding(top = 18.dp)
    ) {
        Box(
            modifier = Modifier
                .size(18.dp)
                .clip(CircleShape)
                .background(AndroidGreen.copy(alpha = 0.16f)),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(6.dp)
                    .clip(CircleShape)
                    .background(AndroidGreen)
            )
        }
        Spacer(modifier = Modifier.height(10.dp))
        Text(
            text = id.padStart(2, '0'),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Black,
            color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.32f),
            letterSpacing = 1.sp
        )
    }
}

@Composable
private fun ExperienceCard(
    experience: dev.alimmz.portfolio.domain.model.Experience,
    modifier: Modifier,
    isWide: Boolean
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    val cardColor by animateColorAsState(
        targetValue = if (isHovered) MaterialTheme.colorScheme.surface else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.7f),
        animationSpec = tween(180),
        label = "experience_card_color"
    )

    Surface(
        modifier = modifier
            .hoverable(interactionSource)
            .border(
                1.dp,
                if (isHovered) AndroidGreen.copy(alpha = 0.65f) else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.08f),
                RoundedCornerShape(20.dp)
            ),
        shape = RoundedCornerShape(20.dp),
        color = cardColor
    ) {
        Column(
            modifier = Modifier.padding(if (isWide) 26.dp else 20.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Text(
                    text = experience.company,
                    style = if (isWide) MaterialTheme.typography.titleLarge else MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Black,
                    color = MaterialTheme.colorScheme.onBackground,
                    modifier = Modifier.weight(1f)
                )
                Text(
                    text = experience.id.padStart(2, '0'),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Black,
                    color = AndroidGreen,
                    letterSpacing = 1.sp
                )
            }

            Spacer(modifier = Modifier.height(12.dp))
            Surface(
                color = AndroidGreen.copy(alpha = 0.14f),
                shape = RoundedCornerShape(50)
            ) {
                Text(
                    text = experience.position.uppercase(),
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
                    style = MaterialTheme.typography.labelSmall,
                    color = AndroidGreen,
                    fontWeight = FontWeight.Black,
                    letterSpacing = 0.8.sp
                )
            }

            Spacer(modifier = Modifier.height(18.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.08f))
            Spacer(modifier = Modifier.height(18.dp))

            experience.description.lineSequence().forEach { line ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 11.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Box(
                        modifier = Modifier
                            .padding(top = 8.dp, end = 10.dp)
                            .size(5.dp)
                            .clip(CircleShape)
                            .background(AndroidGreen)
                    )
                    Text(
                        text = line.trim().removePrefix("•").removePrefix("●").trim(),
                        modifier = Modifier.weight(1f),
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        lineHeight = if (isWide) 25.sp else 24.sp
                    )
                }
            }
        }
    }
}

@Composable
fun StudioFooter(
    profile: dev.alimmz.portfolio.domain.model.Profile?,
    horizontalPadding: androidx.compose.ui.unit.Dp,
    isWide: Boolean
) {
    val uriHandler = LocalUriHandler.current

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = horizontalPadding)
            .padding(top = if (isWide) 30.dp else 10.dp)
    ) {
        HorizontalDivider(color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.10f))

        Spacer(modifier = Modifier.height(40.dp))

        Text(
            text = "LET'S BUILD\nSOMETHING GOOD.",
            style = if (isWide) MaterialTheme.typography.displayMedium.copy(
                fontWeight = FontWeight.Black,
                letterSpacing = (-2).sp
            ) else MaterialTheme.typography.headlineLarge.copy(
                fontWeight = FontWeight.Black,
                letterSpacing = (-1).sp
            ),
            color = MaterialTheme.colorScheme.onBackground,
            lineHeight = if (isWide) 48.sp else 38.sp
        )

        Spacer(modifier = Modifier.height(14.dp))

        Text(
            "Android engineering, product thinking and clean execution.",
            style = if (isWide) MaterialTheme.typography.bodyLarge else MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Spacer(modifier = Modifier.height(28.dp))

        if (isWide) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FooterLink("GITHUB") { profile?.githubUrl?.let { uriHandler.openUri(it) } }
                FooterLink("LINKEDIN") { profile?.linkedinUrl?.let { uriHandler.openUri(it) } }
                FooterLink("EMAIL") { profile?.email?.let { uriHandler.openUri("mailto:$it") } }
                FooterLink("CONTACT") {
                    uriHandler.openUri(CONTACT_URL)
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                FooterLink("GITHUB", Modifier.fillMaxWidth()) { profile?.githubUrl?.let { uriHandler.openUri(it) } }
                FooterLink("LINKEDIN", Modifier.fillMaxWidth()) { profile?.linkedinUrl?.let { uriHandler.openUri(it) } }
                FooterLink("EMAIL", Modifier.fillMaxWidth()) { profile?.email?.let { uriHandler.openUri("mailto:$it") } }
                FooterLink("CONTACT", Modifier.fillMaxWidth()) {
                    uriHandler.openUri(CONTACT_URL)
                }
            }
        }
    }
}

@Composable
private fun FooterLink(
    text: String,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    val borderColor by animateColorAsState(
        if (isHovered) AndroidGreen.copy(alpha = 0.85f)
        else MaterialTheme.colorScheme.onBackground.copy(alpha = 0.10f),
        animationSpec = tween(180),
        label = "footer_border"
    )

    Surface(
        modifier = modifier
            .hoverable(interactionSource)
            .border(
                1.dp,
                borderColor,
                RoundedCornerShape(50)
            )
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        shape = RoundedCornerShape(50),
        color = Color.Transparent
    ) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.padding(
                horizontal = 14.dp,
                vertical = 12.dp
            )
        ) {
            Text(
                text = text,
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onBackground,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
fun FantasyCursor(position: Offset) {
    val animatedX by animateFloatAsState(
        position.x,
        animationSpec = spring(stiffness = Spring.StiffnessLow),
        label = "cursor_x"
    )
    val animatedY by animateFloatAsState(
        position.y,
        animationSpec = spring(stiffness = Spring.StiffnessLow),
        label = "cursor_y"
    )

    Box(
        modifier = Modifier
            .offset {
                IntOffset(position.x.toInt() - 3, position.y.toInt() - 3)
            }
            .size(6.dp)
            .background(AndroidGreen, CircleShape)
    )

    Box(
        modifier = Modifier
            .offset {
                IntOffset(animatedX.toInt() - 16, animatedY.toInt() - 16)
            }
            .size(32.dp)
            .border(1.dp, AndroidGreen.copy(alpha = 0.45f), CircleShape)
            .graphicsLayer { alpha = 0.5f }
    )

    Canvas(
        modifier = Modifier
            .fillMaxSize()
            .alpha(0.06f)
    ) {
        drawCircle(
            brush = Brush.radialGradient(
                colors = listOf(AndroidGreen, Color.Transparent),
                center = Offset(animatedX, animatedY),
                radius = 170f
            ),
            radius = 170f,
            center = Offset(animatedX, animatedY)
        )
    }
}

